import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEOHead } from './SEOHead';

const DEFAULT_BASE_URL = 'https://starteaseai.com';
const SITE_NAME = 'StartEase Agent';

/**
 * BlogListingSEO - SEO component for blog index and category pages
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonical - Canonical URL
 * @param {string} [props.category] - Category name (for category pages)
 * @param {Array} props.posts - Array of blog posts for schema
 * @param {number} props.currentPage - Current pagination page
 * @param {number} props.totalPages - Total number of pages
 * @param {string} [props.baseUrl] - Base URL for the site
 */
export const BlogListingSEO = ({
  title,
  description,
  canonical,
  category = null,
  posts = [],
  currentPage = 1,
  totalPages = 1,
  baseUrl = DEFAULT_BASE_URL,
}) => {
  // Only index the first page of pagination
  const noindex = currentPage > 1;

  // CollectionPage JSON-LD Schema
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description: description,
    url: canonical,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: DEFAULT_BASE_URL,
    },
    ...(posts.length > 0 && {
      hasPart: posts.slice(0, 10).map(post => ({
        '@type': 'BlogPosting',
        headline: post.title,
        url: `${baseUrl}/blog/${post.slug}`,
        ...(post.excerpt && { description: post.excerpt }),
        ...(post.featured_image && { image: post.featured_image }),
        ...(post.published_at && { datePublished: post.published_at }),
        author: {
          '@type': 'Person',
          name: post.author || 'StartEase Team',
        },
      })),
    }),
  };

  // BreadcrumbList JSON-LD Schema
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: DEFAULT_BASE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: `${DEFAULT_BASE_URL}/blog`,
    },
  ];

  if (category) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: category,
      item: canonical,
    });
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
        ogType="website"
        noindex={noindex}
      />
      <Helmet>
        {/* Pagination meta tags */}
        {currentPage > 1 && (
          <link rel="prev" href={`${canonical}?page=${currentPage - 1}`} />
        )}
        {currentPage < totalPages && (
          <link rel="next" href={`${canonical}?page=${currentPage + 1}`} />
        )}

        {/* JSON-LD Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(collectionSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
    </>
  );
};

export default BlogListingSEO;
